import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  SortChangedEvent
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import BlueprintForm from '@/forms/BlueprintForm';
import useBlueprint, {
  useBlueprints,
  useDeleteBlueprint,
  useTaskableTypes
} from '@/hooks/useBlueprint';
import usePagination from '@/hooks/usePagination';
import { Blueprint } from '@/types/blueprint';
import { DIRECTION } from '@/types/sort';
import { Button, Drawer, Input, Select } from '@/ui';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import { OptionType } from '@/ui/components/Form/Select/types';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';
import { resolveGridTheme } from '@/utils/gridTheme';
import {
  BLUEPRINT_ADD,
  BLUEPRINT_CHANGE,
  BLUEPRINT_DELETE,
  hasPermission
} from '@/utils/userPermission';

import { COLUMN_ORDERING_MAP, createColumnDefs } from './columns';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule
]);

export const blueprintsDefaults = {
  direction: DIRECTION.ASC,
  ordering: 'name',
  page: '1'
};

const Blueprints = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, resolvedTheme, darkStyle } = useContext(AppContext);

  const gridTheme = useMemo(
    () => resolveGridTheme(resolvedTheme, darkStyle),
    [resolvedTheme, darkStyle]
  );

  const canAdd = hasPermission({ permissions, requiredPermission: BLUEPRINT_ADD });
  const canChange = hasPermission({ permissions, requiredPermission: BLUEPRINT_CHANGE });
  const canDelete = hasPermission({ permissions, requiredPermission: BLUEPRINT_DELETE });

  const deleteBlueprint = useDeleteBlueprint();

  // 'new' opens the drawer for a blank blueprint; a Blueprint opens it
  // pre-filled for that row; null means the drawer is closed.
  const [drawerBlueprint, setDrawerBlueprint] = useState<Blueprint | 'new' | null>(null);
  const closeDrawer = () => setDrawerBlueprint(null);

  const handleDelete = (blueprint: Blueprint) => {
    if (!window.confirm(`Delete "${blueprint.name}"? This can be restored by an engineer.`)) return;
    deleteBlueprint.mutate(blueprint.id);
  };

  const handleEditSchema = (blueprint: Blueprint) => {
    navigate(`${location.pathname.replace(/\/$/, '')}/${blueprint.id}/form`);
  };

  const activeParams = {
    direction: searchParams.get('direction') ?? blueprintsDefaults.direction,
    ordering: searchParams.get('ordering') ?? blueprintsDefaults.ordering,
    page: searchParams.get('page') ?? blueprintsDefaults.page,
    search: searchParams.get('search') ?? '',
    taskableType: searchParams.get('taskable_type') ?? ''
  };

  const { data: taskableTypes } = useTaskableTypes();
  const taskableTypeOptions: OptionType<string>[] = (taskableTypes ?? []).map((type) => ({
    value: String(type.id),
    label: `${type.app_label}.${type.model}`
  }));
  const selectedTaskableType =
    taskableTypeOptions.find((option) => option.value === activeParams.taskableType) ?? null;

  const handleTaskableTypeChange = (option: OptionType<string> | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (option) next.set('taskable_type', option.value);
      else next.delete('taskable_type');
      next.set('page', '1');
      return next;
    });
  };

  const [searchInput, setSearchInput] = useState(activeParams.search);
  useEffect(() => {
    if (searchInput === activeParams.search) return;
    const timeout = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.set('page', '1');
        return next;
      });
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleSortChanged = (event: SortChangedEvent<Blueprint>) => {
    const sortModel = event.api.getColumnState().find((col) => col.sort);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const colId = sortModel?.colId;
      if (colId && COLUMN_ORDERING_MAP[colId]) {
        next.set('ordering', COLUMN_ORDERING_MAP[colId]);
        next.set('direction', sortModel?.sort === 'asc' ? DIRECTION.ASC : DIRECTION.DESC);
      } else {
        next.set('ordering', blueprintsDefaults.ordering);
        next.set('direction', blueprintsDefaults.direction);
      }
      next.set('page', '1');
      return next;
    });
  };

  const { direction, ordering, page, search, taskableType } = activeParams;
  const requestParams = {
    ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
    page,
    ...(search && { search }),
    ...(taskableType && { taskable_type: taskableType })
  };

  const { data, isPending, isSuccess } = useBlueprints(requestParams);

  const blueprints = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;
  const totalPages = isSuccess ? (data.data?.total_pages ?? 0) : 0;

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +activeParams.page,
    totalPages
  );

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (currentPage !== +blueprintsDefaults.page) next.set('page', currentPage.toString());
      else next.delete('page');
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // The drawer reuses the grid's already-loaded row for editing, but re-fetches
  // the single blueprint so the form has the current form_schema and any fields
  // that only the detail view carries.
  const editingId =
    drawerBlueprint !== null && drawerBlueprint !== 'new' ? drawerBlueprint.id : undefined;
  const { data: drawerBlueprintDetail } = useBlueprint(editingId);
  const drawerBlueprintData =
    drawerBlueprint === 'new' || drawerBlueprint === null
      ? undefined
      : (drawerBlueprintDetail?.data ?? drawerBlueprint);

  return (
    <Container type="full-width-page">
      <div className="flex items-center justify-between mb-3">
        <PageHeader headingText="Blueprints" />
        {canAdd && <Button onClick={() => setDrawerBlueprint('new')}>New blueprint</Button>}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input
            label="Search"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search blueprints…"
            value={searchInput}
          />
          <Select
            clearable
            label="Taskable type"
            options={taskableTypeOptions}
            selectedOption={selectedTaskableType}
            onChange={handleTaskableTypeChange}
            placeholder="All types"
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-500">
            {isPending ? 'Loading…' : `${totalCount} blueprint${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {isPending && <Spinner />}
      <Card className="overflow-hidden p-0" paddingClass="p-0">
        <div className="w-full">
          <AgGridReact<Blueprint>
            theme={gridTheme}
            columnDefs={createColumnDefs({
              canDelete,
              canEdit: canChange,
              onDelete: handleDelete,
              onEdit: setDrawerBlueprint
            })}
            domLayout="autoHeight"
            onSortChanged={handleSortChanged}
            pagination={false}
            rowData={blueprints}
          />
        </div>
      </Card>

      <div className="mt-4">
        <Paginator
          currentPage={currentPage}
          goToPage={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={totalPages}
        />
      </div>

      <Drawer
        open={drawerBlueprint !== null}
        onClose={closeDrawer}
        title={drawerBlueprint === 'new' ? 'Create Blueprint' : 'Edit Blueprint'}>
        <BlueprintForm
          blueprint={drawerBlueprintData}
          onCancel={closeDrawer}
          onEditForm={drawerBlueprintData ? () => handleEditSchema(drawerBlueprintData) : undefined}
          onSuccess={closeDrawer}
        />
      </Drawer>
    </Container>
  );
};

export default Blueprints;
